import { h, Component } from 'preact';
import qs from 'qs';
import Store from 'store/store';
import * as actions from 'store/actions';
import { accessNested, joinClasses, joinPaths, isObject } from 'utils/utils';
import pure from 'utils/pure';
import formatDateTime from 'utils/formatDateTime';
import HTML from 'components/core/html/html';
import Transition from 'components/core/transition/transition';
import { getAbsolutePageUrl } from 'components/core/link/link';
import DatesScroller from 'components/shared/theaters/datesScroller';
import ProviderInterstitial from 'components/shared/theaters/providerInterstitial';
import ProviderSelection from 'components/shared/theaters/providerSelection';
import ScreeningsByFormat from 'components/shared/theaters/screeningsByFormat';
import ScreeningsByTitle from 'components/shared/theaters/screeningsByTitle';
// import DatelessScreenings from 'components/shared/theaters/datelessScreenings';
import s from 'components/shared/theaters/screenings.sss';


export default @pure class Screenings extends Component {

	constructor(props) {
		super(props);
		this.closeAuth = this.closeAuth.bind(this);
		this.showAuth = this.showAuth.bind(this);
	}

	componentWillMount() {
		this.update(this.props);
	}

	componentWillReceiveProps(nextProps){
		if (nextProps.selectedDate !== this.props.selectedDate) {
			this.prevSelectedDate = this.props.selectedDate;
		}
	}

	componentWillUpdate(nextProps) {
		this.update(nextProps);
	}

	closeAuth() {
		Store.emit(actions.SHOW_SCREENING_AUTH, { screening: null, theater: this.props.theater });
	}

	showAuth() {
		const authProviderId = accessNested(this.props, 'pageData.auth.providerId');
		const responsePage = getAbsolutePageUrl('authResponse');

		if (authProviderId && responsePage) {
			const authRoot = accessNested(this.props, 'appData.apis.auth.root', 'https://auth.powster.com');
			const queryString = qs.stringify({
				providerId: authProviderId,
				redirectUri: responsePage
			});

			const authUrl = joinPaths(authRoot, '/auth_start?' + queryString);

			const width = accessNested(this.props, 'pageData.auth.popup.width', 800);
			const height = accessNested(this.props, 'pageData.auth.popup.height', 700);
			const left = window.screen ? (window.screen.width - width) / 2 : 50;
			const top = window.screen ? (window.screen.height - height) / 2 : 50;

			window.open(authUrl, 'auth', `left=${left},top=${top},height=${height},width=${width},resizable`);
		}
	}

	doScroll(dir) {
		const ds = this.dayScroller;
		if (!ds || !ds.addForce) return;
		ds.addForce(dir * ds.base.clientWidth / 20);
	}

	update({ screenings }) {
		const stDates = this.stDates = screenings.filter(s => s.stDate).reduce((a, sc) => {
			if (a.indexOf(sc.stDate) === -1) a.push(sc.stDate);
			if (sc.nextDaySTDate && a.indexOf(sc.nextDaySTDate) === -1) a.push(sc.nextDaySTDate);
			return a;
		}, []);
		stDates.sort((a, b) => a.date < b.date ? -1 : 1);
		const screeningsPerDate = this.screeningsPerDate = stDates.map(() => []); // Array of arrays of size stDates.length

		screenings.forEach(sc => {
			if (!sc.stDate) return;
			const dateI = stDates.indexOf(sc.stDate);
			if (dateI === -1) return;
			screeningsPerDate[dateI].push(sc);
			if (sc.nextDaySTDate) {
				const nextDayDateI = stDates.indexOf(sc.nextDaySTDate);
				if (nextDayDateI === -1) return;
				screeningsPerDate[nextDayDateI].push(sc);
			}
		});

		const isNextDay = (sc, stDate) => (sc.stDate.id === stDate.id && !!sc.nextDaySTDate);
		screeningsPerDate.forEach((screenings, i) => {
			const stDate = stDates[i];
			screenings.sort((a, b) => {
				const nextDayA = isNextDay(a, stDate);
				const nextDayB = isNextDay(b, stDate);
				if (nextDayA !== nextDayB) return nextDayA ? 1 : -1;
				return a.datetime < b.datetime ? -1 : 1;
			});
		});
	}

	/**
	 * Return whether the provider interstitial component should be rendered.
	 *
	 * @param {Screening} activeScreening - the currently active screening
	 * @param {Array.<Screening>} dateScreenings - the list of screenings
	 * @return {boolean} true if the provider interstitial is supposed to be rendered, false otherwise
	 */
	shouldRenderProviderInterstitial(activeScreening, dateScreenings) {
		// Check that the feature is enabled
		const options = accessNested(this.props, 'appData.options', {});
		const useProviderInterstitial = options.useProviderInterstitial;
		const alwaysDisplayInterstitial = options.alwaysDisplayInterstitial;
		if (!useProviderInterstitial && !alwaysDisplayInterstitial) return false;

		const useAuth = !!accessNested(this.props, 'pageData.auth.providerId');
		if (useAuth) return false;

		if (!activeScreening) return false;

		// Check that the component has this screening
		if (!dateScreenings || !Array.isArray(dateScreenings)) return false;
		if (dateScreenings.indexOf(activeScreening) === -1) return false;

		// Check that we have more than 1 provider
		if (!activeScreening.urls || !isObject(activeScreening.urls)) return false;
		const providerCount = Object.keys(activeScreening.urls).length;
		if (providerCount < 1) return false;

		if (alwaysDisplayInterstitial) return true;
		if (providerCount === 1) return false;

		return true;
	}

	/**
	 * Return whether the authentication interstitial component should be rendered.
	 *
	 * @param {Array.<Screening>} dateScreenings - the list of screenings
	 * @return {boolean} true if the authentication interstitial should be rendered, false otherwise
	 */
	shouldRenderAuthInterstitial(dateScreenings) {
		const useAuth = !!accessNested(this.props, 'pageData.auth.providerId');
		if (!useAuth) return false;

		const authScreening = accessNested(this.props, 'theater.authScreening');
		if (!authScreening) return false;

		// Check that the component has this screening
		if (!dateScreenings || !Array.isArray(dateScreenings)) return false;
		if (dateScreenings.indexOf(authScreening) === -1) return false;

		return true;
	}

	renderAuthStep() {
		const {
			theater,
			pageData,
			copy
		} = this.props;

		const { authScreening: screening } = theater;
		const { ctaCopy } = pageData;

		let time = '';
		let format = null;
		// Big copy from providerInterstitial :O - should we store that in the screening somewhere? (e.g. displayTime)
		if (screening.cta || !screening.time) {
			const ctaVal = ctaCopy[screening.cta || 0];
			time = copy[ctaVal] || ctaVal;
		} else {
			const timeNumbers = formatDateTime(screening.datetime, copy.$TIME_FORMAT, copy.$DATETIME_LOCALE);
			let timePeriod = formatDateTime(screening.datetime, copy.$TIME_PERIOD, copy.$DATETIME_LOCALE);

			// if (screening.hour <= 2 && +screening.stDate.date === +date) {
			if (screening.hour <= 2) {
				const nextDay = new Date(screening.datetime);
				nextDay.setDate(nextDay.getDate() + 1);
				timePeriod += ' (' + formatDateTime(nextDay, copy.$DAY_FORMAT, copy.$DATETIME_LOCALE) + ')';
			}

			time = [timeNumbers, <span class={s.period}>{timePeriod}</span>];
			format = accessNested(screening, 'movie.format.label', '');
		}

		const divider = format ? <span class={s.timeFormatDivider} /> : '';
		return {
			content: (
				<div id="screening-auth" class={s.auth}>
					<button class={s.back} onClick={this.closeAuth}>{copy.$CHANGE_TIME}</button>
					<h3 class={s.time}>
						<span class={s.formatTruncating} title={format}>{format}</span>
						{divider}
						<span>{time}</span>
					</h3>
					<div class={s.authContent}>
						<div class={s.info}>{copy.$LOGIN_TEXT}</div>
						<div class={s.button} onClick={this.showAuth}>Log In</div>
					</div>
				</div>
			),
			class: s.thin
		};
	}

	renderStep() {
		const props = this.props;
		const {
			appData,
			theater,
			pageData = {},
			filters,
			copy,
			selectedProvider,
			providers
		} = props;
		let selectedDate = props.selectedDate;
		const activeScreening = theater.activeScreening;

		// Duplicated with render, TODO: avoid that
		const stDates = this.stDates;
		let currIdx = stDates.indexOf(selectedDate);
		if (!selectedDate || currIdx === -1) {
			currIdx = 0;
			selectedDate = stDates[currIdx];
		}

		let entering = s.enteringRight;
		let leaving  = s.leavingLeft;
		if (this.prevSelectedDate) {
			const prevIdx = stDates.indexOf(this.prevSelectedDate);
			if (prevIdx !== -1 && prevIdx > currIdx) {
				entering = s.enteringLeft;
				leaving = s.leavingRight;
			}
		}

		const dateScreenings = this.screeningsPerDate[currIdx];

		const hasAuthInterstitial = this.shouldRenderAuthInterstitial(dateScreenings);
		if (hasAuthInterstitial) {
			return this.renderAuthStep();
		}

		const hasProviderInterstitial = this.shouldRenderProviderInterstitial(activeScreening, dateScreenings);
		if (hasProviderInterstitial) {
			return {
				content: (
					<ProviderInterstitial
						id="provider-interstitial"
						theater={theater}
						activeScreening={activeScreening}
						appData={appData}
						date={selectedDate.date}
						pageData={pageData}
						filters={filters}
					/>
				),
				class: s.thin
			};
		}
		let selectTimeCopy = pageData.selectTimeCopy;
		if (selectTimeCopy && typeof selectTimeCopy === 'object') {
			selectTimeCopy = selectTimeCopy[theater.pid] || selectTimeCopy[theater.id] || selectTimeCopy.default;
		}
		selectTimeCopy = copy[selectTimeCopy] || selectTimeCopy || copy.$SELECT_TIME_TO_PURSHASE;
		if (dateScreenings && dateScreenings.length === 1 && (dateScreenings[0].cta || !dateScreenings[0].time)) {
			let ctaSelectTime = pageData.ctaSelectTime || {};
			let ctaSelectTimeCopy = ctaSelectTime[dateScreenings[0].cta || 0];
			if (ctaSelectTimeCopy) {
				selectTimeCopy = copy[ctaSelectTimeCopy] || ctaSelectTimeCopy;
			}
		}
		let useInterstitial = appData.options.useProviderInterstitial;
		return {
			content: (
				<div
					id="screening-selection"
					class={useInterstitial ? s.listings : s.listingsAndProviderSelection}
				>
					{!useInterstitial &&
						<ProviderSelection
							appData={appData}
							class={s.providers}
							providers={providers}
							selectedProvider={selectedProvider}
							cinemaLogo={theater.exhibitor && theater.exhibitor.logo ? theater.exhibitor.logo : 'default.png'}
						/>
					}
					<HTML class={s.cinemaCta} content={selectTimeCopy} markdown />
					<Transition
						class={s.selectedDateTimesContainer}
						duration={600}
						enteringClass={entering}
						leavingClass={leaving}
						visibleClass={s.visible}
						noInitialTransition
					>
						{this.renderScreenings(selectedDate, dateScreenings)}
					</Transition>
				</div>
			),
			entering: s.enteringTop,
			leaving: s.leavingBottom
		};
	}

	renderScreenings(date, screenings) {
		let props = this.props;
		let ScreeningsRenderer = ScreeningsByFormat;
		if (accessNested(props, 'appData.options.useTitleFilterLegacy')) {
			ScreeningsRenderer = ScreeningsByTitle;
		}
		return (
			<ScreeningsRenderer
				setContentHeight={this.props.setContentHeight}
				ref={$ref => $ref && (this.$renderer = $ref)}
				id={date && date.id}
				stDate={date}
				screenings={screenings}
				appData={props.appData}
				device={props.device}
				isIOS={props.isIOS}
				provider={props.selectedProvider}
				theater={props.theater}
				filters={props.filters}
				pageData={props.pageData}
			/>
		);
	}

	render(props) {
		const {
			appData,
			device,
			screenings,
		} = props;

		let { selectedDate } = props;

		if (!this.stDates) this.update(props);
		const stDates = this.stDates;

		if (stDates.length === 0) {
			return (
				<div class={s.listings} key="dateless">
					{this.renderScreenings(null, screenings)}
				</div>
			);
		}

		let currIdx = stDates.indexOf(selectedDate);
		if (!selectedDate || currIdx === -1) {
			currIdx = 0;
			selectedDate = stDates[currIdx];
		}

		let stepEntering = s.enteringBottom;
		let stepLeaving  = s.leavingTop;

		let step = this.renderStep();
		if (!step || typeof step !== 'object') {
			step = { content: step };
		}

		return (
			<div class={joinClasses(s.ticketing, props.class)}>
				<DatesScroller
					appData={appData}
					device={device}
					screeningsPerDate={this.screeningsPerDate}
					stDates={stDates}
					selectedDate={selectedDate}
					screenings={screenings}
				/>
				<Transition
					class={joinClasses(s.dateTicketing, step.class)}
					duration={600}
					enteringClass={step.entering || stepEntering}
					leavingClass={step.leaving || stepLeaving}
					visibleClass={s.visible}
					noInitialTransition
				>
					{step.content}
				</Transition>
			</div>
		);
	}

}
